/**
 * @file Actions.tsx
 * Contains the set of action buttons for the MoreInfoModal.
 */
import React, { FC, useEffect } from 'react';
import { Button, Skeleton } from 'antd';
import { IMoreInfoModalActionsProps } from './types';
import { useNavigate } from 'react-router-dom';
import {
  useAdd,
  validateSelections,
} from '../../../../hooks/localSchedule/useAdd';
import Swal from 'sweetalert2';
import { useScheduleValidator } from '../../../../hooks/useScheduleValidator';
import { getErrorMessage } from '../../../../hooks/useApi';

const Actions: FC<IMoreInfoModalActionsProps> = ({
  user,
  currentSelections,
  totalStudents,
  totalQualifiedStudents,
  onLoading,
  onClose,
}) => {
  const navigate = useNavigate();

  // Filter out selections without any days, and validate two-day minimum constraints
  const validatedSelections = () => {
    // Basic day selection filter
    const base = validateSelections(currentSelections);
    // Exclude any selection that requires a two-day minimum but has fewer than 2 days
    return base.filter((sel: any) => {
      // If schedule enforces two-day minimum, ensure at least 2 days selected
      if (sel.schedule?.twoDayMinimum) {
        return (sel.totalDays || 0) >= 2;
      }
      return true;
    });
  };

  // Detect any selections that violate the two-day minimum requirement
  const hasTwoDayErrors = currentSelections.some(
    (sel: any) =>
      // Only consider selections where at least one day is chosen and schedule enforces two-day minimum
      sel.schedule?.twoDayMinimum &&
      (sel.totalDays || 0) > 0 &&
      (sel.totalDays || 0) < 2
  );
  const hasPendingPricing = currentSelections.some(
    (sel: any) => (sel.totalDays || 0) > 0 && sel.pricingStatus !== 'ready'
  );
  const scheduleValidator = useScheduleValidator();

  const addToSchedule = useAdd();

  const handleAddToSchedule = () => {
    if (validatedSelections().length > 0) {
      const selectionsToValidate = validatedSelections().map(
        (selection: any) => ({
          ...selection,
          studentId: selection.studentId,
          scheduleId: selection.scheduleId,
        })
      );

      scheduleValidator
        .validateSchedules(selectionsToValidate)
        .then(() => {
          addToSchedule(validatedSelections());
          onClose();
          Swal.fire({
            icon: 'success',
            title: 'Success',
            text: 'Schedules added successfully!',
            confirmButtonText: 'OK',
            confirmButtonColor: '#3085d6',
          });
        })
        .catch((error: any) => {
          console.error(error);
          // Determine error message: prefer API-formatted message, fall back to Error.message, then generic text
          const apiMsg =
            typeof getErrorMessage === 'function'
              ? getErrorMessage(error)
              : undefined;
          const message =
            apiMsg ||
            error?.message ||
            'An error occurred, please try again later.';
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: message,
            confirmButtonText: 'OK',
          });
        });
    }
  };

  useEffect(() => {
    if (onLoading) {
      onLoading(scheduleValidator.isValidating);
    }
  }, [scheduleValidator.isValidating, onLoading]);

  if (scheduleValidator.isValidating) {
    return <Skeleton active={true} data-testid="loading-skeleton" />;
  }

  return (
    <div style={{ marginTop: 16 }}>
      <table className="w-100">
        <tbody>
          <tr>
            {(user || totalStudents > 0) && (
              <td style={{ width: 'calc(50% - 20px)' }}>
                <Button
                  block
                  type="primary"
                  className="btn-sac"
                  style={{ marginRight: 8 }}
                  onClick={() => navigate('/')}
                >
                  Edit Students
                </Button>
              </td>
            )}
            {totalQualifiedStudents > 0 && (
              <>
                <td className="text-center" style={{ width: '24px' }}>
                  <span style={{ paddingLeft: '8px', paddingRight: '8px' }}>
                    or
                  </span>
                </td>
                <td style={{ width: 'calc(50% - 20px)' }}>
                  <Button
                    block
                    type="primary"
                    className="btn-sac"
                    onClick={handleAddToSchedule}
                    disabled={
                      // Disable if no valid selections or any two-day-min requirement is unmet
                      validatedSelections().length === 0 ||
                      hasTwoDayErrors ||
                      hasPendingPricing
                    }
                  >
                    Add to Schedule
                  </Button>
                </td>
              </>
            )}
          </tr>
        </tbody>
      </table>
    </div>
  );
};

export default Actions;
